// components/Drawer/Drawer.tsx
import React, { useEffect } from "react";
import "./Drawer.css";

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  position?: "left" | "right";
  width?: string;
  children: React.ReactNode;
  title?: string;
  showCloseButton?: boolean;
}

const Drawer: React.FC<DrawerProps> = ({
  isOpen,
  onClose,
  position = "right",
  width = "400px",
  children,
  title,
  showCloseButton = true,
}) => {
  // Close drawer on Escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      // Prevent body scroll when drawer is open
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="drawer-overlay">
      {/* Backdrop */}
      <div className="drawer-backdrop" onClick={onClose} />

      {/* Drawer Panel */}
      <div className={`drawer-panel drawer-${position}`} style={{ width }}>
        {/* Header */}
        {(title || showCloseButton) && (
          <div className="drawer-header">
            {title && <h3 className="drawer-title">{title}</h3>}
            {showCloseButton && (
              <button
                className="drawer-close-btn"
                onClick={onClose}
                aria-label="Drawer schließen"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M18 6L6 18M6 6L18 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}
          </div>
        )}

        {/* Content */}
        <div className="drawer-content">{children}</div>
      </div>
    </div>
  );
};

export default Drawer;
